import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Badge, Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import NotesIcon from '@mui/icons-material/Notes';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { getWeekDays, formatDayLabel, DAY_KEYS, buildVacationDateSet } from '../utils/dateUtils';
import FixedSnackbar from './FixedSnackbar';
import LoadingButton from './LoadingButton';

export default function TimecardWeekView({ userEmail, weekStart, schedule, timecard, onSaved, vacations }) {
  const { t, lang } = useTranslation();

  const [days, setDays] = useState({});
  const [totalHours, setTotalHours] = useState(0);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogDay, setNoteDialogDay] = useState(null);
  const [noteDialogText, setNoteDialogText] = useState('');

  const weekDays = getWeekDays(weekStart);

  useEffect(() => {
    if (timecard) {
      setDays(timecard.days || {});
      setTotalHours(timecard.total_hours || 0);
      setStatus(timecard.status || 'draft');
    } else {
      const initial = {};
      weekDays.forEach((d) => { initial[d] = { hours: 0, notes: '' }; });
      setDays(initial);
      setTotalHours(0);
      setStatus('draft');
    }
  }, [timecard, weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let sum = 0;
    Object.values(days).forEach((d) => { sum += parseFloat(d.hours) || 0; });
    setTotalHours(Math.round(sum * 100) / 100);
  }, [days]);

  const isWorkDay = useCallback((dateStr) => {
    if (!schedule || !schedule.days) return true;
    const dayIdx = new Date(dateStr + 'T00:00:00').getDay();
    const adjustedIdx = dayIdx === 0 ? 6 : dayIdx - 1;
    const key = DAY_KEYS[adjustedIdx];
    return schedule.days[key] && schedule.days[key].active !== false;
  }, [schedule]);

  const vacationSet = React.useMemo(() => buildVacationDateSet(vacations), [vacations]);

  const isEditable = status === 'draft' || status === 'rejected';

  const handleDayChange = (dateStr, field, value) => {
    if (!isEditable) return;
    setDays((prev) => ({
      ...prev,
      [dateStr]: { ...(prev[dateStr] || {}), [field]: value }
    }));
  };

  const openNoteDialog = (dateStr) => {
    setNoteDialogDay(dateStr);
    setNoteDialogText(days[dateStr]?.notes || '');
    setNoteDialogOpen(true);
  };

  const saveNote = () => {
    if (noteDialogDay) {
      handleDayChange(noteDialogDay, 'notes', noteDialogText);
    }
    setNoteDialogOpen(false);
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.TIMECARD_SAVE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_email: userEmail, week_start: weekStart, days, total_hours: totalHours })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_error_save');
      }
      const data = await res.json();
      setStatus(data.timecard?.status || 'draft');
      setSnackbar({ open: true, message: t('calendar_success_saved'), severity: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_error_save'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const saveRes = await fetch(API_ENDPOINTS.TIMECARD_SAVE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_email: userEmail, week_start: weekStart, days, total_hours: totalHours })
      });
      if (!saveRes.ok) {
        const err = await saveRes.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_error_save');
      }

      const submitRes = await fetch(API_ENDPOINTS.TIMECARD_SUBMIT(userEmail, weekStart), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!submitRes.ok) {
        const err = await submitRes.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_error_submit');
      }
      setStatus('submitted');
      setSnackbar({ open: true, message: t('calendar_success_submitted'), severity: 'success' });
      setConfirmSubmit(false);
      if (onSaved) onSaved();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_error_submit'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const statusColor = { draft: 'default', submitted: 'warning', approved: 'success', rejected: 'error' };

  return (
    <Box>
      {status !== 'draft' && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={t(`calendar_status_${status}`)}
            color={statusColor[status] || 'default'}
            sx={{ fontWeight: 600 }}
          />
          {status === 'rejected' && timecard?.rejection_reason && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {t('calendar_rejection_reason')}: {timecard.rejection_reason}
            </Typography>
          )}
          {(status === 'submitted' || status === 'approved') && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {status === 'submitted' ? t('calendar_already_submitted') : t('calendar_already_approved')}
            </Typography>
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {weekDays.map((dateStr) => {
          const workDay = isWorkDay(dateStr);
          const isVacationDay = vacationSet.has(dateStr);
          const dayData = days[dateStr] || { hours: 0, notes: '' };
          return (
            <Grid item xs={12} sm={6} md key={dateStr} sx={{ display: 'flex' }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  width: '100%',
                  minHeight: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: isVacationDay ? 'rgba(76, 175, 80, 0.08)' : !workDay ? 'grey.100' : 'rgba(25, 118, 210, 0.06)',
                  borderColor: isVacationDay ? 'success.light' : !workDay ? 'grey.300' : 'primary.light',
                  transition: 'all 0.15s',
                  '&:hover': { boxShadow: 1 }
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: isVacationDay ? 'success.dark' : !workDay ? 'grey.600' : 'primary.main', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatDayLabel(dateStr, lang)}
                </Typography>
                {isVacationDay ? (
                  <Chip label={t('calendar_schedule_vacation')} size="small" color="success" variant="outlined" sx={{ fontWeight: 500 }} />
                ) : !workDay ? (
                  <Chip label={t('calendar_day_off')} size="small" sx={{ bgcolor: 'grey.200', color: 'grey.600', fontWeight: 500 }} />
                ) : (
                  <>
                    <TextField
                      label={t('calendar_hours_label')}
                      type="number"
                      size="small"
                      fullWidth
                      value={dayData.hours || ''}
                      onChange={(e) => handleDayChange(dateStr, 'hours', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 24, step: 0.5 }}
                      disabled={!isEditable}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                    <Tooltip title={dayData.notes || t('calendar_notes_label')}>
                      <IconButton
                        size="small"
                        onClick={() => openNoteDialog(dateStr)}
                        disabled={!isEditable && !dayData.notes}
                        sx={{ mt: 0.5 }}
                      >
                        <Badge
                          variant="dot"
                          invisible={!dayData.notes}
                          color="primary"
                        >
                          <NotesIcon fontSize="small" color={dayData.notes ? 'primary' : 'action'} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('calendar_total_hours')}: {totalHours}
        </Typography>
        {isEditable && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveDraft}
              loading={loading}
              loadingLabel={t('calendar_save_draft')}
              sx={{ textTransform: 'none' }}
            >
              {t('calendar_save_draft')}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => setConfirmSubmit(true)}
              loading={loading}
              loadingLabel={t('calendar_submit_timecard')}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
            >
              {t('calendar_submit_timecard')}
            </LoadingButton>
          </Box>
        )}
      </Box>

      <Dialog open={confirmSubmit} onClose={() => setConfirmSubmit(false)}>
        <DialogTitle>{t('calendar_confirm_submit')}</DialogTitle>
        <DialogContent>
          <Typography>{t('calendar_submit_confirm')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('calendar_total_hours')}: {totalHours}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmit(false)}>{t('cancel') || 'Cancel'}</Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={loading}
            loadingLabel={t('calendar_submit_timecard')}
          >
            {t('calendar_submit_timecard')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('calendar_notes_label')}
          {noteDialogDay && (
            <Typography variant="caption" display="block" color="text.secondary">
              {formatDayLabel(noteDialogDay, lang)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={noteDialogText}
            onChange={(e) => setNoteDialogText(e.target.value)}
            placeholder={t('calendar_notes_label')}
            InputProps={{ readOnly: !isEditable }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>{t('cancel') || 'Cancel'}</Button>
          {isEditable && (
            <Button variant="contained" onClick={saveNote}>
              {t('calendar_notes_label')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
      />
    </Box>
  );
}
